import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const PUBLIC_API_BASE = `${API_BASE_URL}/api/shipping-fees`;
const ADMIN_API_BASE = `${API_BASE_URL}/api/admin/shipping-fees`;

function getAuthHeaders() {
  const token = localStorage.getItem('jwtToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function useShippingFee(country) {
  const [fee, setFee] = useState(null);
  const [currency, setCurrency] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allFees, setAllFees] = useState([]); // For admin table

  // Fetch fee for a specific country (public route)
  useEffect(() => {
    if (!country || country === 'default') return;
    setLoading(true);
    fetch(`${PUBLIC_API_BASE}?country=${encodeURIComponent(country)}`)
      .then(res => res.json())
      .then(data => {
        setFee(data.fee);
        setCurrency(data.currency || '');
        setError(null);
      })
      .catch(err => setError(err.message || 'Failed to fetch shipping fee'))
      .finally(() => setLoading(false));
  }, [country]);

  // Fetch all fees (for admin, but public GET is allowed)
  const fetchAllFees = useCallback(() => {
    setLoading(true);
    fetch(PUBLIC_API_BASE)
      .then(res => res.json())
      .then(data => {
        setAllFees(data);
        setError(null);
      })
      .catch(err => setError(err.message || 'Failed to fetch shipping fees'))
      .finally(() => setLoading(false));
  }, []);

  // Add a new shipping fee (admin)
  const addFee = async (country, fee, currency) => {
    setLoading(true);
    try {
      const res = await fetch(ADMIN_API_BASE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ country, fee: Number(fee), currency }),
      });
      if (!res.ok) throw new Error('Failed to add shipping fee');
      fetchAllFees();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update a shipping fee (admin)
  const updateFee = async (id, country, fee, currency) => {
    setLoading(true);
    try {
      const res = await fetch(`${ADMIN_API_BASE}/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ country, fee: Number(fee), currency }),
      });
      if (!res.ok) throw new Error('Failed to update shipping fee');
      fetchAllFees();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete a shipping fee (admin)
  const deleteFee = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${ADMIN_API_BASE}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete shipping fee');
      fetchAllFees();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    fee, currency, loading, error,
    allFees, fetchAllFees, addFee, updateFee, deleteFee
  };
} 